// src/components/opportunity/OpportunityWorkspace.tsx
//
// Opportunity workspace shell. Loads one opportunity (with its active
// pricing snapshot and revisions) via useOpportunityWorkspace, renders
// a tab bar (Overview / Pricing / Documents / Revisions / Generated),
// and dispatches to the right tab component. Tabs that mutate (status
// change, save revision) call `state.refresh()` to repull fresh data.
//
// Pricing stays mounted (hidden when inactive) so in-progress edits
// survive tab switches; Save proposal auto-persists dirty pricing
// before rendering the PDF.

import { useCallback, useEffect, useRef, useState } from "react";
import { OPPORTUNITY_STATUS_LABELS } from "@/types/opportunity";
import { useOpportunityWorkspace } from "@/components/opportunity/useOpportunityWorkspace";
import { useDualReadProductTypes } from "@/hooks/useDualReadProductTypes";
import { formatOpportunityProductType } from "@/lib/opportunity/productTypeLabel";
import { OpportunityOverviewTab } from "@/components/opportunity/OpportunityOverviewTab";
import {
  OpportunityPricingTab,
  type OpportunityPricingHandle,
} from "@/components/opportunity/OpportunityPricingTab";
import { OpportunityDocumentsTab } from "@/components/opportunity/OpportunityDocumentsTab";
import { OpportunityRevisionsTab } from "@/components/opportunity/OpportunityRevisionsTab";
import { OpportunityGeneratedTab } from "@/components/opportunity/OpportunityGeneratedTab";
import { OpportunityServiceClosedCallout } from "@/components/opportunity/OpportunityServiceClosedCallout";
import { useOpportunityServiceOfferingMatch } from "@/components/opportunity/useOpportunityServiceOfferingMatch";
import {
  GenerateProposalButton,
  type EnsurePricingSavedResult,
} from "@/components/opportunity/GenerateProposalButton";
import { OpportunityTodosTab } from "@/components/opportunity/OpportunityTodosTab";

type TabId = "overview" | "pricing" | "documents" | "revisions" | "generated" | "todos";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "pricing", label: "Pricing" },
  { id: "documents", label: "Documents" },
  { id: "revisions", label: "Revisions" },
  { id: "generated", label: "Generated documents" },
  { id: "todos", label: "Todo list" },
];

export type OpportunityWorkspaceProps = {
  opportunityId: string;
  initialTab?: TabId;
  onBack: () => void;
};

export function OpportunityWorkspace({ opportunityId, initialTab, onBack }: OpportunityWorkspaceProps) {
  const state = useOpportunityWorkspace(opportunityId);
  const productTypes = useDualReadProductTypes();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab ?? "overview");
  const [generatedRefreshKey, setGeneratedRefreshKey] = useState(0);
  const [todosRefreshKey, setTodosRefreshKey] = useState(0);
  const [documentsFocusSlotKey, setDocumentsFocusSlotKey] = useState<string | null>(null);
  const [documentsSourceGeneratedId, setDocumentsSourceGeneratedId] = useState<string | null>(
    null,
  );
  const pricingHandleRef = useRef<OpportunityPricingHandle | null>(null);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [opportunityId, initialTab]);

  const productType = state.data?.opportunity.product_type ?? "";
  const { matchedOffering } = useOpportunityServiceOfferingMatch(productType);

  const ensurePricingSaved = useCallback(async (): Promise<EnsurePricingSavedResult> => {
    const handle = pricingHandleRef.current;
    if (handle?.isDirty()) {
      const saved = await handle.saveIfDirty();
      if (!saved.ok) return saved;
      const data = await state.refreshAsync();
      if (!data?.active_snapshot) {
        return {
          ok: false,
          error: "Pricing saved, but the opportunity could not be reloaded.",
        };
      }
      return {
        ok: true,
        opportunity: data.opportunity,
        activeSnapshot: data.active_snapshot,
        revisions: data.revisions,
      };
    }
    const current = state.data;
    if (!current?.active_snapshot) {
      return {
        ok: false,
        error: "Save a pricing revision before generating a proposal.",
      };
    }
    return {
      ok: true,
      opportunity: current.opportunity,
      activeSnapshot: current.active_snapshot,
      revisions: current.revisions,
    };
  }, [state.data, state.refreshAsync]);

  if (state.loading && !state.data) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-6 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
        Loading opportunity…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-indigo-700 hover:underline dark:text-indigo-300"
        >
          ← Back to opportunities
        </button>
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {state.error}
        </div>
      </div>
    );
  }

  if (!state.data) return null;

  const { opportunity, active_snapshot: activeSnapshot, revisions } = state.data;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-indigo-700 hover:underline dark:text-indigo-300"
          >
            ← Back to opportunities
          </button>
          <h3 className="mt-1 text-lg font-bold text-gray-900 dark:text-zinc-100 truncate">
            {opportunity.customer_company || opportunity.customer_name || opportunity.project_name || "Opportunity"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            {formatOpportunityProductType(opportunity.product_type, productTypes)}
            <span className="mx-1">·</span>
            {OPPORTUNITY_STATUS_LABELS[opportunity.status] ?? opportunity.status}
            {opportunity.project_name && (opportunity.customer_company || opportunity.customer_name) ? (
              <>
                <span className="mx-1">·</span>
                {opportunity.project_name}
              </>
            ) : null}
          </p>
        </div>
        <GenerateProposalButton
          opportunity={opportunity}
          activeSnapshot={activeSnapshot}
          revisions={revisions}
          ensurePricingSaved={ensurePricingSaved}
          onGenerated={() => {
            setGeneratedRefreshKey((k) => k + 1);
            setActiveTab("generated");
          }}
        />
      </div>

      <OpportunityServiceClosedCallout
        opportunity={opportunity}
        matchedOffering={matchedOffering}
        onOpenDocuments={() => setActiveTab("documents")}
      />

      <nav className="flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-zinc-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={
              "px-3 py-1.5 text-sm rounded-t-lg border-b-2 -mb-px " +
              (activeTab === t.id
                ? "border-indigo-500 text-indigo-700 font-semibold dark:text-indigo-300"
                : "border-transparent text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-100")
            }
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div>
        {activeTab === "overview" ? (
          <OpportunityOverviewTab
            opportunity={opportunity}
            activeSnapshot={activeSnapshot}
            revisions={revisions}
            onChanged={() => {
              state.refresh();
              setTodosRefreshKey((k) => k + 1);
            }}
            onArchived={onBack}
            onDeleted={onBack}
          />
        ) : null}
        {/* Keep Pricing mounted so unsaved edits survive tab switches and
            Save proposal can auto-persist them before rendering the PDF. */}
        <div
          className={activeTab === "pricing" ? undefined : "hidden"}
          aria-hidden={activeTab !== "pricing"}
        >
          <OpportunityPricingTab
            opportunity={opportunity}
            activeSnapshot={activeSnapshot}
            onSaved={state.refresh}
            onOpenDocumentsTab={() => setActiveTab("documents")}
            pricingHandleRef={pricingHandleRef}
          />
        </div>
        {activeTab === "documents" ? (
          <OpportunityDocumentsTab
            opportunity={opportunity}
            focusSlotKey={documentsFocusSlotKey}
            sourceGeneratedOutputId={documentsSourceGeneratedId}
          />
        ) : null}
        {activeTab === "revisions" ? (
          <OpportunityRevisionsTab
            opportunity={opportunity}
            revisions={revisions}
            activeSnapshot={activeSnapshot}
          />
        ) : null}
        {activeTab === "generated" ? (
          <OpportunityGeneratedTab
            opportunity={opportunity}
            revisions={revisions}
            refreshKey={generatedRefreshKey}
            onUploadSigned={({ generatedOutputId, suggestedSlotKey }) => {
              setDocumentsFocusSlotKey(suggestedSlotKey);
              setDocumentsSourceGeneratedId(generatedOutputId);
              setActiveTab("documents");
            }}
          />
        ) : null}
        {activeTab === "todos" ? (
          <OpportunityTodosTab opportunity={opportunity} reloadKey={todosRefreshKey} />
        ) : null}
      </div>
    </div>
  );
}
